/**
 * IpamCascadeDeleteModal — aviso de borrado en cascada de un bloque o subred.
 *
 * Muestra exactamente qué se va a borrar (subredes, rangos, IPs, reservas,
 * grupos virtuales) y qué elementos se van a desasociar (nombre del CI, IP y
 * interfaz). El botón de borrar queda bloqueado hasta marcar la casilla.
 */
import React, { useState } from 'react';

function CountChip({ label, value, tone = 'secondary' }) {
  if (!value) return null;
  return (
    <span className={`badge text-bg-${tone}`} style={{ fontSize: '0.75em' }}>
      {value} {label}
    </span>
  );
}

export default function IpamCascadeDeleteModal({
  title,
  subtitle,
  preview,
  busy = false,
  error = '',
  onConfirm,
  onClose,
}) {
  const [confirmed, setConfirmed] = useState(false);
  const counts = preview?.counts || {};
  const elements = preview?.elements || [];
  const warnings = preview?.warnings || [];
  const subnets = preview?.subnets || [];

  const hasContent = (counts.ips || 0) + (counts.subnets || 0) > 0;

  const summary = (() => {
    if (!hasContent) return 'No hay nada colgando: se borra solo el registro.';
    const bits = [];
    if (counts.subnets) bits.push(`${counts.subnets} subred(es)`);
    if (counts.ranges) bits.push(`${counts.ranges} rango(s)`);
    if (counts.ips) bits.push(`${counts.ips} IP(s)`);
    if (counts.reservations) bits.push(`${counts.reservations} reserva(s) DHCP`);
    if (counts.virtual_groups) bits.push(`${counts.virtual_groups} grupo(s) virtual(es)`);
    return bits.join(' · ');
  })();

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1060 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 640 }}>
        <div className="modal-content shadow-lg">
          <div className="modal-header py-3 border-bottom">
            <div>
              <h6 className="modal-title fw-bold mb-0 text-danger">{title}</h6>
              {subtitle && (
                <small className="text-muted" style={{ fontFamily: 'monospace' }}>
                  {subtitle}
                </small>
              )}
            </div>
            <button className="btn-close" onClick={onClose} disabled={busy} />
          </div>

          <div className="modal-body px-4 py-3">
            {error && <div className="alert alert-danger py-2 small">{error}</div>}

            <p className="mb-2 small">
              Se eliminará en cascada todo su contenido y los elementos que tengan
              una IP suya quedarán <strong>desasociados</strong> (los equipos no se
              borran).
            </p>

            <div className="d-flex flex-wrap gap-1 mb-3">
              <CountChip label="subred(es)" value={counts.subnets} tone="primary" />
              <CountChip label="rango(s)" value={counts.ranges} tone="secondary" />
              <CountChip label="IP(s)" value={counts.ips} tone="info" />
              <CountChip label="IP(s) ocupada(s)" value={counts.assigned_ips} tone="warning" />
              <CountChip label="reserva(s) DHCP" value={counts.reservations} tone="secondary" />
              <CountChip label="grupo(s) virtual(es)" value={counts.virtual_groups} tone="secondary" />
            </div>
            <div className="small text-muted mb-3">{summary}</div>

            {subnets.length > 0 && (
              <div className="mb-3">
                <div className="fw-semibold small mb-1">Subredes afectadas</div>
                <div
                  className="border rounded px-2 py-1"
                  style={{ maxHeight: 120, overflowY: 'auto', fontSize: '0.82em' }}
                >
                  {subnets.map(s => (
                    <div key={s.id} className="d-flex justify-content-between">
                      <span style={{ fontFamily: 'monospace' }}>{s.cidr}</span>
                      <span className="text-muted">
                        {s.ips_count} IP(s)
                        {s.delegated_to_company_name ? ` · delegada a ${s.delegated_to_company_name}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {elements.length > 0 && (
              <div className="mb-3">
                <div className="fw-semibold small mb-1">
                  Elementos que se desasociarán ({elements.length})
                </div>
                <div
                  className="border rounded px-2 py-1"
                  style={{ maxHeight: 170, overflowY: 'auto', fontSize: '0.82em' }}
                >
                  {elements.map((el, idx) => (
                    <div key={`${el.object_type}-${el.object_id}-${idx}`} className="d-flex justify-content-between gap-2">
                      <span className="text-truncate">
                        {el.label}
                        <span className="text-muted"> ({el.object_type})</span>
                        {el.interface_ref ? <span className="text-muted"> · {el.interface_ref}</span> : null}
                      </span>
                      <span style={{ fontFamily: 'monospace' }}>
                        {el.ip}{el.is_primary ? ' ★' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {warnings.length > 0 && (
              <div className="alert alert-warning py-2 px-3 small mb-3">
                <ul className="mb-0 ps-3">
                  {warnings.map((w, idx) => (
                    <li key={idx}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="ipam-cascade-confirm"
                checked={confirmed}
                onChange={e => setConfirmed(e.target.checked)}
                disabled={busy}
              />
              <label className="form-check-label small" htmlFor="ipam-cascade-confirm">
                Entiendo que esta acción no se puede deshacer
                {counts.ips ? ` y que se perderán ${counts.ips} IP(s)` : ''}
                {counts.elements ? `, desasociando ${counts.elements} elemento(s)` : ''}.
              </label>
            </div>
          </div>

          <div className="modal-footer py-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={busy}>
              Cancelar
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => onConfirm && onConfirm()}
              disabled={busy || !confirmed}
            >
              {busy ? 'Eliminando…' : 'Eliminar en cascada'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
